<template>
  <div>
    <component v-if="resolvedComponent && isActive" :is="resolvedComponent" v-bind="componentProps" />
  </div>
</template>

<script setup>
import { ref, computed, defineAsyncComponent, onMounted, inject } from "vue";
import { usePage } from "@inertiajs/vue3";

const props = defineProps({
  widget: {
    type: Object,
    required: true,
  },
});

// Strip path/extension to get a plain component name e.g. "Carousel"
const getCleanComponentName = (path) => {
  if (!path) return "";
  let name = path.includes("/") ? path.split("/").pop() : path;
  if (name.startsWith("Web/")) name = name.substring(4);
  if (name.endsWith(".vue")) name = name.slice(0, -4);
  return name;
};

const resolvedComponent = ref(null);

const isActive = computed(() =>
  [true, 1, "1", "true"].includes(props.widget.is_active)
);

// Injected data from Home.vue via provide()
const formasi   = inject("formasi", {});
const news      = inject("news", {});
const slider    = inject("slider", {});
const agenda    = inject("agenda", []);
const grafik    = inject("grafik", []);
const galery    = inject("galery", []);
const profil    = inject("profil", []);
const linkmedsos = inject("linkmedsos", []);
const jurusanstat = inject("jurusanstat", null);

const page = usePage();
const apps = computed(() => page?.props?.apps ?? {});

// Ambil jurusanstat langsung dari page props (selalu fresh per kunjungan),
// fallback ke provide/inject bila ada. Hindari nilai basi dari provide.
const jurusanstatResolved = computed(() => {
  const src = page?.props?.jurusanstat ?? jurusanstat ?? null;
  return (src && src.data) || src || {};
});

// Map component name → which injected props to forward
const componentProps = computed(() => {
  const base = props.widget.config || {};
  const name = getCleanComponentName(props.widget.component || "");

  const propMap = {
    Carousel:      { ...base, slider },
    HeroSection:   { ...base, profil },
    BlogSection:   { ...base, news },
    MedsosLink:    { ...base, linkmedsos },
    AuthorSection: { ...base, formasi },
    AgendaSection: { ...base, agenda },
    GraphSection:  { ...base, grafik },
    CarouselGalery:{ ...base, galery },
    JurusanStats:  { ...base, jurusan: jurusanstatResolved.value },
    LayananStats:  { ...base, layanan: props.widget.layanan || [] },
  };

  return propMap[name] ?? base;
});

// Lazy-load map — only components that exist in this project
const componentMap = {
  Carousel:       () => import("../Carousel.vue"),
  HeroSection:    () => import("../HeroSection.vue"),
  AuthorSection:  () => import("../AuthorSection.vue"),
  AgendaSection:  () => import("../AgendaSection.vue"),
  BlogSection:    () => import("../BlogSection.vue"),
  GraphSection:   () => import("../GraphSection.vue"),
  JurusanStats:   () => import("../JurusanStats.vue"),
  LayananStats:   () => import("../LayananStats.vue"),
  CarouselGalery: () => import("../CarouselGalery.vue"),
  MedsosLink:     () => import("../MedsosLink.vue"),
};

onMounted(() => {
  const componentPath = props.widget.component;
  if (!componentPath) return;

  const name = getCleanComponentName(componentPath);
  const loader = componentMap[name]
    ?? (() => import(`../${name}.vue`));

  resolvedComponent.value = defineAsyncComponent({
    loader,
    delay: 200,
    timeout: 20000,
    onError(err) {
      console.error(`Widget load failed: ${componentPath}`, err);
    },
  });
});
</script>
